import { resolveBracketMention } from '@/lib/power-mention-index';

export type BracketEmbedTextPart =
  | { type: 'text'; value: string }
  | { type: 'mention'; value: string; slug: string; label: string }
  | { type: 'ace'; value: string; slug: string; label: string }
  | { type: 'smilies'; value: string; slug: string }
  | { type: 'pawns'; value: string; slug: string };

/** @deprecated Prefer BracketEmbedTextPart */
export type PowerMentionTextPart = BracketEmbedTextPart;

/**
 * BBCodes:
 * - `[[smilies:slug]]` / `[[pawns:slug]]` — embeds de galeria
 * - `[[ace:slug]]` — menção Ace
 * - `[[nomedopower]]` — menção power
 */
const BRACKET_MENTION_RE = /\[\[\s*([^[\]\n]+?)\s*\]\]/g;

function parseBracketInner(inner: string): BracketEmbedTextPart | null {
  const trimmed = inner.trim();
  if (!trimmed) return null;

  const typed = /^(smilies|pawns|ace)\s*:\s*(.+)$/i.exec(trimmed);
  if (typed) {
    const kind = typed[1].toLowerCase();
    const rawSlug = typed[2].trim();
    const slug = rawSlug.toLowerCase().replace(/\s+/g, '');
    if (!slug) return null;
    if (kind === 'smilies') return { type: 'smilies', value: `[[${trimmed}]]`, slug };
    if (kind === 'pawns') return { type: 'pawns', value: `[[${trimmed}]]`, slug };
    if (kind === 'ace') {
      return {
        type: 'ace',
        value: `[[${trimmed}]]`,
        slug,
        label: rawSlug,
      };
    }
  }

  const resolved = resolveBracketMention(trimmed);
  if (!resolved) return null;
  return {
    type: 'mention',
    value: `[[${trimmed}]]`,
    slug: resolved.slug,
    label: resolved.label,
  };
}

export function splitTextWithPowerMentions(text: string): BracketEmbedTextPart[] {
  if (!text) return [];

  const parts: BracketEmbedTextPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const re = new RegExp(BRACKET_MENTION_RE.source, BRACKET_MENTION_RE.flags);
  while ((match = re.exec(text)) !== null) {
    const start = match.index;
    const matched = match[0];
    if (start > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, start) });
    }
    const parsed = parseBracketInner(match[1]);
    if (parsed) {
      parts.push({ ...parsed, value: matched });
    } else {
      parts.push({ type: 'text', value: matched });
    }
    lastIndex = start + matched.length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
}

export type BracketEmbedHtmlPart =
  | { type: 'html'; value: string }
  | BracketEmbedTextPart;

/** @deprecated Prefer BracketEmbedHtmlPart */
export type PowerMentionHtmlPart = BracketEmbedHtmlPart;

/** Processa HTML simples do CMS preservando tags e aplicando embeds só em nós de texto. */
export function splitHtmlWithPowerMentions(html: string): BracketEmbedHtmlPart[] {
  if (!html.trim()) return [];

  const segments = html.split(/(<[^>]+>)/g).filter(Boolean);
  const parts: BracketEmbedHtmlPart[] = [];

  for (const segment of segments) {
    if (segment.startsWith('<')) {
      parts.push({ type: 'html', value: segment });
      continue;
    }
    for (const piece of splitTextWithPowerMentions(segment)) {
      if (piece.type === 'text') {
        if (piece.value) parts.push(piece);
      } else {
        parts.push(piece);
      }
    }
  }

  return parts;
}
